import { getDeclination } from 'shared/utils/getDeclination'
import {
  IStockConfirmationStatus,
  TStockConfirmationSource
} from 'models/seller'

export type TStockConfirmationLevel = 'danger' | 'warning' | 'ok' | 'none'

// Предупреждение в кабинете загорается за два дня до отключения
const WARNING_BEFORE_DAYS = 2

export const STOCK_TERMS_HINT =
  'Срок зависит от последнего способа обновления: вручную и Excel — 8 дней, МойСклад, 1С, InSales и API — 5 дней. Если остатки не обновлялись ни разу, срок считается от появления первого товара.'

export const stockConfirmationSourceLabels: Record<
  TStockConfirmationSource,
  string
> = {
  MANUAL: 'Вручную',
  EXCEL: 'Excel',
  MOYSKLAD: 'МойСклад',
  ONE_C: '1С',
  INSALES: 'InSales',
  API: 'API'
}

export const formatStockConfirmationDate = (value?: string | null) => {
  if (!value) {
    return '—'
  }

  return new Intl.DateTimeFormat('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  }).format(new Date(value))
}

export const getStockConfirmationLevel = (
  status: IStockConfirmationStatus | null
): TStockConfirmationLevel => {
  if (!status) return 'none'
  if (status.isOutdated) return 'danger'
  // Дней нет только у магазина без товаров и без обновлений — срок не идёт
  if (status.daysWithoutConfirmation == null) return 'none'
  if (!status.lastConfirmedAt) return 'warning'
  if (
    status.daysWithoutConfirmation >=
    status.disableAfterDays - WARNING_BEFORE_DAYS
  ) {
    return 'warning'
  }

  return 'ok'
}

export const getStockConfirmationTag = (
  status: IStockConfirmationStatus | null
) => {
  const level = getStockConfirmationLevel(status)
  const isNeverUpdated = Boolean(status && !status.lastConfirmedAt)

  if (level === 'danger') {
    return {
      color: 'error',
      label: isNeverUpdated ? 'Остатки не обновлялись' : 'Требуется обновление'
    }
  }

  if (level === 'warning') {
    return {
      color: 'warning',
      label: isNeverUpdated
        ? 'Остатки не обновлялись'
        : 'Скоро потребуется обновление'
    }
  }

  if (level === 'none') {
    return { color: 'default', label: 'Нет обновлений' }
  }

  return { color: 'success', label: 'Остатки актуальны' }
}

export const getStockConfirmationSourceText = (
  status: IStockConfirmationStatus | null
) =>
  status?.lastConfirmedSource
    ? stockConfirmationSourceLabels[status.lastConfirmedSource]
    : '—'

export const getStockDaysText = (status: IStockConfirmationStatus | null) => {
  if (status?.daysWithoutConfirmation == null) return '—'

  const days = status.daysWithoutConfirmation
  const term = status.disableAfterDays

  return days > term
    ? `${days} дн. при сроке ${term}`
    : `${days} из ${term} дн.`
}

const formatDays = (days: number) =>
  `${days} ${getDeclination(days, ['дней', 'день', 'дня'])}`

export const getStockAlertText = (status: IStockConfirmationStatus | null) => {
  const level = getStockConfirmationLevel(status)
  if (!status || (level !== 'danger' && level !== 'warning')) return null

  const days = formatDays(status.daysWithoutConfirmation ?? 0)
  const term = formatDays(status.disableAfterDays)

  if (level === 'danger') {
    return status.lastConfirmedAt
      ? `Остатки не обновлялись ${days} при сроке ${term}. Товары магазина отключаются и не публикуются, пока остатки не обновлены.`
      : 'Остатки ни разу не обновлялись. Товары магазина отключаются и не публикуются, пока остатки не обновлены.'
  }

  // Без обратного отсчёта: проверка идёт раз в сутки, и до отключения
  // может остаться и час, и почти двое суток
  return status.lastConfirmedAt
    ? `Остатки не обновлялись ${days}. Товары магазина отключатся, когда без обновления пройдёт ${term}.`
    : `Остатки ещё ни разу не обновлялись. Товары магазина отключатся, когда с появления первого товара пройдёт ${term}.`
}
